import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from './Button';
import { fetchDatasets, fetchDatasetLocations, fetchDatasetsMetadata, fetchFeatureSegments, renameDataset, deleteDataset } from '../services/datasetsService';
import { useError } from '../contexts/ErrorContext';
import { locationsToGeoJSONFeatureCollection } from '../utils/geoExport';
import Modal from './Modal';
import ExportFormatModal from './ExportFormatModal';
import './DataExplorer.css';

const DataExplorer = () => {
  const navigate = useNavigate();
  const { addError } = useError();
  const [selectedData, setSelectedData] = useState(null);
  const [datasets, setDatasets] = useState([]);
  const [datasetMetaById, setDatasetMetaById] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exportingId, setExportingId] = useState(null); // Track which dataset is being exported
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportModalItem, setExportModalItem] = useState(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deletingName, setDeletingName] = useState('');
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Filter datasets based on search query
  const filteredDatasets = useMemo(() => {
    if (!searchQuery.trim()) {
      return datasets;
    }
    const query = searchQuery.toLowerCase().trim();
    return datasets.filter(item => {
      const name = (item.name || '').toLowerCase();
      const id = (item.id || '').toLowerCase();
      return name.includes(query) || id.includes(query);
    });
  }, [datasets, searchQuery]);

  useEffect(() => {
    let cancelled = false;
    const loadDatasets = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchDatasets(addError);
        if (data && data.length > 0) {
          if (!cancelled) setDatasets(data);

          // Fetch metadata (segments + duration) before hiding the loading state.
          const ids = data.map((d) => d && d.id).filter(Boolean);
          try {
            const metas = await fetchDatasetsMetadata(ids, addError);
            if (!cancelled) {
              const byId = {};
              if (Array.isArray(metas)) {
                metas.forEach((m) => {
                  if (m && m.id) byId[m.id] = m;
                });
              }
              setDatasetMetaById(byId);
            }
          } catch {
            // Error already routed to ErrorContext by the service; keep UI resilient.
          }
        } else {
          setError('No datasets found. The datasets folder may be empty.');
        }
      } catch (err) {
        const errorMsg = err.message || 'Failed to connect to backend. Make sure the server is running on port 3001.';
        setError(errorMsg);
        console.error('Failed to load datasets:', err);
      } finally {
        setLoading(false);
      }
    };

    loadDatasets();
    return () => {
      cancelled = true;
    };
  }, [addError]);

  const formatDuration = (seconds) => {
    if (typeof seconds !== 'number' || !Number.isFinite(seconds)) return '—';
    const s = Math.max(0, Math.floor(seconds));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const ss = s % 60;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${ss}s`;
    return `${ss}s`;
  };

  const handleExplore = (data) => {
    // Navigate to dataset page - you can customize which datasets open the map
    // For now, all datasets navigate to their page
    navigate(`/dataset/${encodeURIComponent(data.id)}`);
  };

  const openRename = (item, e) => {
    if (e) e.stopPropagation();
    setRenamingId(item?.id || null);
    setRenameValue(item?.name || '');
    setRenameOpen(true);
  };

  const closeRename = () => {
    if (renameSaving) return;
    setRenameOpen(false);
    setRenamingId(null);
    setRenameValue('');
  };

  const handleSaveRename = async () => {
    const datasetId = renamingId;
    const nextName = String(renameValue || '').trim();
    if (!datasetId) return;
    if (!nextName) {
      addError('Dataset name cannot be empty', null, 'Data Explorer');
      return;
    }
    try {
      setRenameSaving(true);
      const updated = await renameDataset(datasetId, nextName, addError);
      if (updated && updated.id) {
        setDatasets((prev) =>
          prev.map((d) => (d && d.id === updated.id ? { ...d, name: updated.name } : d))
        );
      }
      closeRename();
    } catch (err) {
      // Error already sent to ErrorContext by service
      console.error('Rename failed:', err);
    } finally {
      setRenameSaving(false);
    }
  };

  const openDelete = (item, e) => {
    if (e) e.stopPropagation();
    setDeletingId(item?.id || null);
    setDeletingName(item?.name || item?.id || '');
    setDeleteOpen(true);
  };

  const closeDelete = () => {
    if (deleteSaving) return;
    setDeleteOpen(false);
    setDeletingId(null);
    setDeletingName('');
  };

  const handleConfirmDelete = async () => {
    const datasetId = deletingId;
    if (!datasetId) return;
    try {
      setDeleteSaving(true);
      const res = await deleteDataset(datasetId, addError);
      const deletedId = res?.deletedId || datasetId;
      setDatasets((prev) => prev.filter((d) => d && d.id !== deletedId));
      setDatasetMetaById((prev) => {
        const next = { ...prev };
        delete next[deletedId];
        return next;
      });
      closeDelete();
    } catch (err) {
      console.error('Delete failed:', err);
    } finally {
      setDeleteSaving(false);
    }
  };

  // Format timestamp for GPX (ISO 8601 format)
  const formatGPXTime = (timestamp) => {
    // timestamp is in seconds since 1970
    const date = new Date(timestamp * 1000);
    return date.toISOString().replace(/\.\d{3}Z$/, 'Z');
  };

  // Convert locations to GPX format (Xcode compatible waypoint format)
  const convertLocationsToGPX = (locations) => {
    if (!locations || locations.length === 0) {
      return null;
    }

    // Build GPX content with Xcode-compatible format
    let gpxContent = `<?xml version="1.0"?>
<gpx version="1.1" creator="Offboard Data Explorer">
`;

    // Add waypoints
    locations.forEach((loc) => {
      const { lat, lon, timestamp, altitude } = loc;
      
      let timeStr = '';
      if (timestamp) {
        timeStr = `
        <time>${formatGPXTime(timestamp)}</time>`;
      }
      
      let eleStr = '';
      if (altitude != null) {
        eleStr = `
        <ele>${altitude}</ele>`;
      }
      
      gpxContent += `    <wpt lat="${lat}" lon="${lon}">${timeStr}${eleStr}
    </wpt>
`;
    });

    gpxContent += `</gpx>`;

    return gpxContent;
  };

  // Handle export for a specific dataset
  const handleExportDataset = async (item, e) => {
    e.stopPropagation(); // Prevent card click
    
    if (exportingId) return; // Already exporting
    
    setExportingId(item.id);
    
    try {
      // Fetch locations for this dataset
      const locations = await fetchDatasetLocations(item.id, addError);
      
      if (!locations || locations.length === 0) {
        addError('No locations found in this dataset', null, 'Export');
        return;
      }
      
      // Convert to GPX
      const gpxContent = convertLocationsToGPX(locations);
      
      if (!gpxContent) {
        addError('Failed to generate GPX content', null, 'Export');
        return;
      }
      
      // Create blob and trigger download
      const blob = new Blob([gpxContent], { type: 'application/gpx+xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Create filename from dataset name (sanitize for filename)
      const filename = `${item.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.gpx`;
      a.download = filename;
      
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      const errorMsg = err.message || 'Failed to export dataset';
      addError(errorMsg, err.stack, 'Export');
      console.error('Export error:', err);
    } finally {
      setExportingId(null);
    }
  };

  // Download full dataset as a single GeoJSON (consolidated, preserves all fields)
  const handleDownloadGeoJson = async (item, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (exportingId) return;
    setExportingId(item.id);
    try {
      const [locations, segments] = await Promise.all([
        fetchDatasetLocations(item.id, addError),
        fetchFeatureSegments(item.id, addError).catch(() => [])
      ]);
      if (!locations || locations.length === 0) {
        addError('No locations found in this dataset', null, 'Export');
        return;
      }
      const geojson = locationsToGeoJSONFeatureCollection(locations, segments);
      const blob = new Blob([JSON.stringify(geojson, null, 2)], { type: 'application/geo+json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const filename = `${item.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.geojson`;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      const errorMsg = err.message || 'Failed to download dataset as GeoJSON';
      addError(errorMsg, err.stack, 'Export');
      console.error('Download GeoJSON error:', err);
    } finally {
      setExportingId(null);
    }
  };

  const openExportModal = (item, e) => {
    e.stopPropagation();
    setExportModalItem(item);
    setExportModalOpen(true);
  };

  const handleExportFormatChoice = (format) => {
    if (!exportModalItem) return;
    const item = exportModalItem;
    setExportModalItem(null);
    if (format === 'gpx') {
      handleExportDataset(item, { stopPropagation: () => {} });
    } else {
      handleDownloadGeoJson(item, { stopPropagation: () => {} });
    }
  };

  return (
    <div className="data-explorer">
      <div className="data-explorer-header">
        <h2>Data Explorer</h2>
        <div className="data-explorer-search">
          <input
            type="text"
            placeholder="Search datasets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="data-explorer-search-input"
          />
        </div>
      </div>

      <div className="data-explorer-content">
        <div className="data-list">
          <h3>Available Datasets {filteredDatasets.length !== datasets.length && `(${filteredDatasets.length} of ${datasets.length})`}</h3>
          {loading && <p>Loading datasets...</p>}
          {error && <p className="error-message">Error: {error}</p>}
          {!loading && !error && datasets.length === 0 && (
            <p>No datasets found. Make sure the backend server is running and the datasets folder contains dataset directories.</p>
          )}
          {!loading && !error && datasets.length > 0 && filteredDatasets.length === 0 && (
            <p>No datasets found matching "{searchQuery}"</p>
          )}
          {!loading && !error && filteredDatasets.length > 0 && (
            <div className="data-grid">
              {filteredDatasets.map((item) => (
                (() => {
                  const meta = datasetMetaById[item.id] || null;
                  const segmentCountsByType = meta && typeof meta.segmentCountsByType === 'object' ? meta.segmentCountsByType : null;
                  const runSegments =
                    segmentCountsByType && typeof segmentCountsByType.run === 'number' ? segmentCountsByType.run : null;
                  const liftSegments =
                    segmentCountsByType && typeof segmentCountsByType.lift === 'number' ? segmentCountsByType.lift : null;
                  const durationSeconds = meta && typeof meta.durationSeconds === 'number' ? meta.durationSeconds : null;
                  const durationLabel = durationSeconds === null ? 'Duration: —' : `Duration: ${formatDuration(durationSeconds)}`;

                  return (
                <div 
                  key={item.id} 
                  className="data-card data-card-clickable"
                  onClick={() => handleExplore(item)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="data-card-header">
                    <div className="data-segment-badges">
                      <span className="data-segment-badge data-segment-badge-run">
                        Run: {runSegments === null ? '—' : runSegments}
                      </span>
                      <span className="data-segment-badge data-segment-badge-lift">
                        Lift: {liftSegments === null ? '—' : liftSegments}
                      </span>
                    </div>
                    <div className="data-card-header-right">
                      <span className="data-size">{durationLabel}</span>
                      <div className="data-card-actions" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="data-card-icon-btn"
                          aria-label="Rename dataset"
                          title="Rename"
                          onClick={(e) => openRename(item, e)}
                          disabled={renameSaving && renamingId === item.id}
                        >
                          ✎
                        </button>
                        <button
                          type="button"
                          className="data-card-icon-btn data-card-icon-btn-danger"
                          aria-label="Delete dataset"
                          title="Delete"
                          onClick={(e) => openDelete(item, e)}
                          disabled={deleteSaving && deletingId === item.id}
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="data-card-body">
                    <h4>{item.name}</h4>
                    <p className="data-date">
                      {item.jsonFileCount} files • Last updated: {item.date}
                    </p>
                  </div>
                  <div className="data-card-footer" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="primary"
                      size="small"
                      onClick={() => handleExplore(item)}
                    >
                      Explore
                    </Button>
                    <Button
                      variant="outline"
                      size="small"
                      onClick={(e) => openExportModal(item, e)}
                      disabled={exportingId === item.id}
                    >
                      {exportingId === item.id ? 'Exporting...' : 'Export'}
                    </Button>
                  </div>
                </div>
                  );
                })()
              ))}
            </div>
          )}
        </div>

        {selectedData && (
          <div className="data-detail">
            <div className="data-detail-header">
              <h3>{selectedData.name}</h3>
              <Button
                variant="outline"
                size="small"
                onClick={() => setSelectedData(null)}
              >
                Close
              </Button>
            </div>
            <div className="data-detail-content">
                <div className="data-info">
                  <div className="info-row">
                    <span className="info-label">JSON Files:</span>
                    <span className="info-value">{selectedData.jsonFileCount}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Date:</span>
                    <span className="info-value">{selectedData.date}</span>
                  </div>
                </div>
              <div className="data-preview">
                <p>Data preview will appear here...</p>
              </div>
            </div>
          </div>
        )}
      </div>

      <Modal
        isOpen={renameOpen}
        onClose={closeRename}
        title="Rename dataset"
        subtitle={renamingId ? `Dataset id: ${renamingId}` : null}
      >
        <div className="dataset-rename-modal">
          <div className="dataset-rename-field">
            <label htmlFor="dataset-rename-input">Name</label>
            <input
              id="dataset-rename-input"
              type="text"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              placeholder="Enter a display name"
              disabled={renameSaving}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveRename();
                if (e.key === 'Escape') closeRename();
              }}
            />
          </div>
          <div className="dataset-rename-actions">
            <Button variant="outline" size="small" onClick={closeRename} disabled={renameSaving}>
              Cancel
            </Button>
            <Button variant="primary" size="small" onClick={handleSaveRename} disabled={renameSaving}>
              {renameSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={deleteOpen}
        onClose={closeDelete}
        title="Delete dataset"
        subtitle={deletingId ? `Dataset id: ${deletingId}` : null}
      >
        <div className="dataset-delete-modal">
          <div className="dataset-delete-warning">
            This will permanently delete <span className="dataset-delete-name">{deletingName}</span> and all files in it.
          </div>
          <div className="dataset-delete-actions">
            <Button variant="outline" size="small" onClick={closeDelete} disabled={deleteSaving}>
              Cancel
            </Button>
            <Button variant="danger" size="small" onClick={handleConfirmDelete} disabled={deleteSaving}>
              {deleteSaving ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </div>
      </Modal>

      <ExportFormatModal
        isOpen={exportModalOpen}
        onClose={() => { setExportModalOpen(false); setExportModalItem(null); }}
        onSelectFormat={handleExportFormatChoice}
        disabled={!!exportingId}
      />
    </div>
  );
};

export default DataExplorer;

